import { useSearchParams } from "react-router";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useRequiredEventRouteContext } from "@/shared/presentation/hooks/useEventRouteContext";
import type { AppNavigation } from "@/shared/presentation/hooks/useAppNavigation";
import { FeatureKey } from "@shared/schemas/feature-key.schemas";
import {
  EventIdPublic,
  EventIdPublicOrSlug,
  EventParticipantIdPublic,
  UserIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import { PROFILE_EDITABLE_FIELDS } from "@shared/definitions/profile-user-edit-locked-fields-feature.definition";
import {
  STORAGE_FILE_TYPE_CONFIGS,
  StorageFileType,
} from "@shared/schemas/storage-file.schemas";
import { ParticipantPreferencesSchema } from "@shared/schemas/participant-preferences.schemas";
import type {
  ProfileUserEditLockedFieldsFeatureConfig,
  ProfileEditableFieldType,
} from "@shared/definitions/profile-user-edit-locked-fields-feature.definition";
import type { LocalizedText } from "@shared/schemas/localized-text.schemas";
import type { EventPublicView } from "@shared/schemas/event.schemas";
import type { EventParticipantSelfView } from "@shared/schemas/event-participant-views.schemas";
import type { ParticipantPreferences } from "@shared/schemas/participant-preferences.schemas";
import type { UpdateParticipantBody } from "@shared/schemas/event-participant-write.schemas";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { i18n } from "@frontend/i18n";
import { useFeatureConfigs } from "@frontend/shared/features/feature-config/application/hooks/use-feature-configs";

function mutationPending(
  mutation: { readonly isPending: boolean } | undefined,
): boolean {
  return mutation?.isPending ?? false;
}

const PROFILE_MGMT_TAB_PROFILE = "profile" as const;
const PROFILE_MGMT_TAB_PREFERENCES = "preferences" as const;
type ProfileMgmtTabType =
  | typeof PROFILE_MGMT_TAB_PROFILE
  | typeof PROFILE_MGMT_TAB_PREFERENCES;

export interface ExternalEditConfig {
  readonly url: string;
  readonly text: string;
}

export interface ProfileEditState {
  readonly participant: EventParticipantSelfView | undefined;
  readonly event: EventPublicView | undefined;
  readonly lockedFields: readonly ProfileEditableFieldType[];
  readonly instructions: string | null;
  readonly externalEdit: ExternalEditConfig | null;
  readonly eventIdPublic: string | undefined;
  readonly participantIdPublic: string | undefined;
  readonly userIdPublic: UserIdPublic | null;
  readonly isEventSubdomain: boolean;
  readonly activeTab: ProfileMgmtTabType;
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  readonly isUploadingPhoto: boolean;
  readonly isRemovingPhoto: boolean;
  readonly error: Error | null;
  readonly saveError: Error | null;
  readonly canNavigateToDirectory: boolean;
  readonly preferences: ParticipantPreferences | undefined;
  readonly isLoadingPreferences: boolean;
  readonly preferencesError: Error | null;
  readonly isOptingIn: boolean;
}

export interface ProfileEditActions {
  readonly onSave: (formData: UpdateParticipantBody) => Promise<void>;
  readonly onUploadPhoto: (file: File) => Promise<void>;
  readonly onRemovePhoto: () => Promise<void>;
  readonly onNavigateToDirectory: () => void;
  readonly setActiveTab: (tab: ProfileMgmtTabType) => void;
  readonly onPreferencesFieldChange: (data: ParticipantPreferences) => void;
  readonly onOptInRemember: () => Promise<void>;
}

export interface ProfileEditControllerOutput {
  readonly state: ProfileEditState;
  readonly actions: ProfileEditActions;
  readonly nav: AppNavigation;
}

export function useProfileEditController(): ProfileEditControllerOutput {
  const [searchParams, setSearchParams] = useSearchParams();

  const eventContext = useRequiredEventRouteContext();
  const eventIdPublic = eventContext.eventIdPublic;
  const eventIdPublicStr = EventIdPublic.unwrap(eventIdPublic);

  const userActions = useUserActions();
  const eventActions = userActions.useForEvent({
    eventIdPublicOrSlug: EventIdPublicOrSlug.unsafeCreate(
      eventContext.eventIdPublicOrSlug,
    ),
  });
  const participantActions = eventActions.useForParticipant({ self: true });
  const mutations = participantActions.mutations;

  const userIdPublic = userActions.user?.idPublic ?? null;

  const initialTab = useMemo((): ProfileMgmtTabType => {
    const tabParam = searchParams.get("tab");
    if (tabParam === PROFILE_MGMT_TAB_PREFERENCES) {
      return PROFILE_MGMT_TAB_PREFERENCES;
    }
    return PROFILE_MGMT_TAB_PROFILE;
  }, [searchParams]);

  const [activeTab, setActiveTabState] =
    useState<ProfileMgmtTabType>(initialTab);

  const setActiveTab = useCallback(
    (tab: ProfileMgmtTabType): void => {
      setActiveTabState(tab);
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (tab === PROFILE_MGMT_TAB_PROFILE) {
            updated.delete("tab");
          } else {
            updated.set("tab", tab);
          }
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const participantIdPublicTyped =
    participantActions.participant?.idPublic ?? undefined;

  const eventQuery = {
    data: eventContext.event,
    isLoading: eventContext.isLoading,
  };
  const nav = eventContext.nav;

  const featureConfigs = useFeatureConfigs({
    context: { eventIdPublic },
    enabled: eventIdPublicStr !== "",
  });

  const resolvedConfig = useMemo(():
    | ProfileUserEditLockedFieldsFeatureConfig
    | undefined => {
    if (eventIdPublicStr === "") {
      return undefined;
    }
    const config = featureConfigs.getFeatureConfig(
      FeatureKey.PROFILE_USER_EDIT_LOCKED_FIELDS,
    );
    if (config === undefined) {
      applicationLogger.warn(
        "[profile-edit] Feature config unavailable for locked fields, failing closed",
        {
          eventIdPublic: eventIdPublicStr,
          featureKey: FeatureKey.PROFILE_USER_EDIT_LOCKED_FIELDS,
        },
      );
    }
    return config?.config;
  }, [eventIdPublicStr, featureConfigs]);

  const lockedFields = useMemo((): readonly ProfileEditableFieldType[] => {
    if (resolvedConfig === undefined) {
      return [...PROFILE_EDITABLE_FIELDS];
    }
    return resolvedConfig.lockedFields;
  }, [resolvedConfig]);

  const locale = i18n.getLocale();

  const instructions = useMemo((): string | null => {
    const value = resolvedConfig?.instructions;
    return value != null
      ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- LocalizedTextConfigSchema uses z.string() for lang, but resolve() only needs content + translations
        LocalizedTextUtils.resolve(value as LocalizedText, locale)
      : null;
  }, [resolvedConfig, locale]);

  const externalEdit = useMemo((): ExternalEditConfig | null => {
    if (
      resolvedConfig?.externalEdit === undefined ||
      resolvedConfig.externalEdit === null
    ) {
      return null;
    }
    return {
      url: resolvedConfig.externalEdit.url,
      text: LocalizedTextUtils.resolve(
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- LocalizedTextConfigSchema uses z.string() for lang, but resolve() only needs content + translations
        resolvedConfig.externalEdit.text as LocalizedText,
        locale,
      ),
    };
  }, [resolvedConfig, locale]);

  const preferencesQuery = participantActions.useGetPreferences({
    eventIdPublic,
    participantIdPublic:
      participantIdPublicTyped ?? EventParticipantIdPublic.unsafeCreate(""),
    enabled: participantIdPublicTyped !== undefined,
  });

  const updateParticipant = mutations?.updateParticipant;
  const updatePreferences = mutations?.updatePreferences;
  const uploadPhotoMutation = mutations?.uploadPhoto;
  const removePhotoMutation = mutations?.removePhoto;
  const optInRememberMutation = mutations?.optInRemember;

  const isOptingIn = mutationPending(optInRememberMutation);
  const isUploadingPhoto = mutationPending(uploadPhotoMutation);
  const isRemovingPhoto = mutationPending(removePhotoMutation);
  const [saveError, setSaveError] = useState<Error | null>(null);

  const onSave = useCallback(
    async (formData: UpdateParticipantBody): Promise<void> => {
      if (participantIdPublicTyped === undefined || eventIdPublicStr === "") {
        setSaveError(new Error(i18n.t("user.profile.form.errorMissingId")));
        return;
      }
      setSaveError(null);
      try {
        await updateParticipant?.mutateAsync({
          eventIdPublic: eventIdPublic,
          participantIdPublic: participantIdPublicTyped,
          data: formData,
        });
      } catch (error: unknown) {
        const errorObj =
          error instanceof Error ? error : new Error(String(error));
        applicationLogger.error("Save failed", "profile-edit", errorObj);
        setSaveError(errorObj);
      }
    },
    [
      participantIdPublicTyped,
      eventIdPublic,
      eventIdPublicStr,
      updateParticipant,
    ],
  );

  const avatarConfig = STORAGE_FILE_TYPE_CONFIGS[StorageFileType.AVATAR_IMAGE];

  const onUploadPhoto = useCallback(
    async (file: File): Promise<void> => {
      if (participantIdPublicTyped === undefined || eventIdPublicStr === "") {
        setSaveError(new Error(i18n.t("user.profile.form.errorMissingId")));
        return;
      }
      if (!avatarConfig.allowedMimeTypes.includes(file.type)) {
        setSaveError(new Error(i18n.t("user.profile.form.invalidPhotoType")));
        return;
      }
      if (file.size > avatarConfig.maxSizeBytes) {
        const maxSize =
          Math.round((avatarConfig.maxSizeBytes / (1024 * 1024)) * 10) / 10;
        setSaveError(
          new Error(i18n.t("user.profile.form.photoTooLarge", { maxSize })),
        );
        return;
      }
      setSaveError(null);
      try {
        await uploadPhotoMutation?.mutateAsync({
          eventIdPublic: eventIdPublic,
          participantIdPublic: participantIdPublicTyped,
          file,
        });
      } catch (error: unknown) {
        const errorObj =
          error instanceof Error ? error : new Error(String(error));
        applicationLogger.error(
          "Photo upload failed",
          "profile-edit",
          errorObj,
        );
        setSaveError(errorObj);
      }
    },
    [
      participantIdPublicTyped,
      eventIdPublic,
      eventIdPublicStr,
      avatarConfig,
      uploadPhotoMutation,
    ],
  );

  const onRemovePhoto = useCallback(async (): Promise<void> => {
    if (participantIdPublicTyped === undefined || eventIdPublicStr === "") {
      setSaveError(new Error(i18n.t("user.profile.form.errorMissingId")));
      return;
    }
    setSaveError(null);
    try {
      await removePhotoMutation?.mutateAsync({
        eventIdPublic: eventIdPublic,
        participantIdPublic: participantIdPublicTyped,
      });
    } catch (error: unknown) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      applicationLogger.error("Photo remove failed", "profile-edit", errorObj);
      setSaveError(errorObj);
    }
  }, [
    participantIdPublicTyped,
    eventIdPublic,
    eventIdPublicStr,
    removePhotoMutation,
  ]);

  const lastSavedPrefsRef = useRef<string>(
    preferencesQuery.data !== undefined
      ? JSON.stringify(
          ParticipantPreferencesSchema.parse(preferencesQuery.data),
        )
      : "",
  );
  const prefsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The work the debounce is sitting on, so unmounting can still send it.
  const pendingPrefsSaveRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (preferencesQuery.data !== undefined) {
      lastSavedPrefsRef.current = JSON.stringify(
        ParticipantPreferencesSchema.parse(preferencesQuery.data),
      );
    }
  }, [preferencesQuery.data]);

  useEffect(
    () => (): void => {
      if (prefsDebounceRef.current !== null) {
        clearTimeout(prefsDebounceRef.current);
        // Flush rather than drop: toggling a preference and navigating away
        // inside the debounce window would otherwise lose the change silently,
        // and the request outlives the component that started it.
        pendingPrefsSaveRef.current?.();
      }
    },
    [],
  );

  const onPreferencesFieldChange = useCallback(
    (data: ParticipantPreferences): void => {
      const parsed = ParticipantPreferencesSchema.safeParse(data);
      if (!parsed.success) {
        return;
      }
      const serialized = JSON.stringify(parsed.data);
      if (serialized === lastSavedPrefsRef.current) {
        return;
      }
      if (prefsDebounceRef.current !== null) {
        clearTimeout(prefsDebounceRef.current);
      }
      const validData = parsed.data;
      const save = (): void => {
        pendingPrefsSaveRef.current = null;
        if (participantIdPublicTyped === undefined || eventIdPublicStr === "") {
          setSaveError(new Error(i18n.t("user.profile.form.errorMissingId")));
          return;
        }
        setSaveError(null);
        void (async (): Promise<void> => {
          try {
            await updatePreferences?.mutateAsync({
              eventIdPublic: eventIdPublic,
              participantIdPublic: participantIdPublicTyped,
              data: validData,
            });
            lastSavedPrefsRef.current = serialized;
          } catch (error: unknown) {
            const errorObj =
              error instanceof Error ? error : new Error(String(error));
            applicationLogger.error(
              "Preferences save failed",
              "profile-edit",
              errorObj,
            );
            setSaveError(errorObj);
          }
        })();
      };
      pendingPrefsSaveRef.current = save;
      prefsDebounceRef.current = setTimeout(save, 200);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutateAsync is a stable reference in TanStack Query v5; using the full mutation object causes identity changes on every state transition (idle→pending→success), triggering cascading API calls
    [
      participantIdPublicTyped,
      eventIdPublic,
      eventIdPublicStr,
      updatePreferences?.mutateAsync,
    ],
  );

  const onOptInRemember = useCallback(async (): Promise<void> => {
    if (participantIdPublicTyped === undefined || eventIdPublicStr === "") {
      setSaveError(new Error(i18n.t("user.profile.form.errorMissingId")));
      return;
    }
    setSaveError(null);
    try {
      await optInRememberMutation?.mutateAsync({
        eventIdPublic: eventIdPublic,
        participantIdPublic: participantIdPublicTyped,
      });
    } catch (error: unknown) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      applicationLogger.error(
        "Opt-in remember failed",
        "profile-edit",
        errorObj,
      );
      setSaveError(errorObj);
    }
  }, [
    participantIdPublicTyped,
    eventIdPublic,
    eventIdPublicStr,
    optInRememberMutation,
  ]);

  const onNavigateToDirectory = useCallback((): void => {
    if (participantIdPublicTyped !== undefined) {
      nav.toParticipantProfile(participantIdPublicTyped);
    }
  }, [nav, participantIdPublicTyped]);

  const isLoading =
    eventActions.isLoading || featureConfigs.isLoading || eventQuery.isLoading;

  const error = eventActions.error ?? featureConfigs.error ?? null;

  return {
    state: {
      participant: participantActions.participant ?? undefined,
      event: eventQuery.data,
      lockedFields,
      instructions,
      externalEdit,
      eventIdPublic: eventIdPublicStr,
      participantIdPublic:
        participantIdPublicTyped !== undefined
          ? EventParticipantIdPublic.unwrap(participantIdPublicTyped)
          : undefined,
      userIdPublic,
      isEventSubdomain: eventContext.isEventSubdomain,
      activeTab,
      isLoading,
      isSaving: mutationPending(updateParticipant),
      isUploadingPhoto,
      isRemovingPhoto,
      error,
      saveError,
      canNavigateToDirectory: participantIdPublicTyped !== undefined,
      preferences: preferencesQuery.data,
      isLoadingPreferences: preferencesQuery.isLoading,
      preferencesError: preferencesQuery.error ?? null,
      isOptingIn,
    },
    actions: {
      onSave,
      onUploadPhoto,
      onRemovePhoto,
      onNavigateToDirectory,
      setActiveTab,
      onPreferencesFieldChange,
      onOptInRemember,
    },
    nav,
  };
}
